import { useState, useEffect, useRef } from "react";

const BeforeAfterComparison = ({
  className = "",
  onSectionClick = () => {},
  showHeader = true,
  autoAnimate = true,
}: {
  className?: string;
  onSectionClick?: (section: string) => void;
  showHeader?: boolean;
  autoAnimate?: boolean;
}) => {
  const [particleCount, setParticleCount] = useState(4);
  const containerRef = useRef<HTMLDivElement>(null);
  const particleIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    if (!autoAnimate) return;
    const observer = new window.IntersectionObserver(
      () => {
        // Entry is observed but not used for now
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [autoAnimate]);

  // Dynamic particle creation
  useEffect(() => {
    if (!autoAnimate) return;
    particleIntervalRef.current = setInterval(() => {
      setParticleCount((prev) => (prev + 1) % 8); // Cycle particles
    }, 2000);
    return () => {
      if (particleIntervalRef.current) {
        clearInterval(particleIntervalRef.current);
      }
    };
  }, [autoAnimate]);

  // Add interactive effects when container updates
  useEffect(() => {
    const handleNoteItemHover = (e: Event) => {
      const target = e.target as HTMLElement;
      if (e.type === "mouseenter") {
        target.style.transform = "translateX(0) scale(1.02)";
        target.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.2)";
      } else {
        target.style.transform = "translateX(0) scale(1)";
        target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
      }
    };
    // Add hover effects to note items
    const noteItems = document.querySelectorAll(".note-item");
    noteItems.forEach((item) => {
      item.addEventListener("mouseenter", handleNoteItemHover);
      item.addEventListener("mouseleave", handleNoteItemHover);
    });
    return () => {
      noteItems.forEach((item) => {
        item.removeEventListener("mouseenter", handleNoteItemHover);
        item.removeEventListener("mouseleave", handleNoteItemHover);
      });
    };
  }, []); // Using an empty dependency array as this effect only needs to run once on mount

  const handleSectionClick = (section: string) => {
    onSectionClick(section);
    // Add special click effects
    const element = document.querySelector(
      `.${section}-section`
    ) as HTMLElement;
    if (element) {
      element.style.animation =
        section === "before"
          ? "shake 0.5s ease-in-out"
          : "glow 0.5s ease-in-out";
      setTimeout(() => {
        element.style.animation =
          section === "before"
            ? "shake 2s ease-in-out infinite"
            : "glow 3s ease-in-out infinite";
      }, 500);
    }
  };

  const styles = {
    wrapper: {
      minHeight: "80vh",
      marginTop: "-180px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      position: "relative" as const,
      overflow: "hidden",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      width: "100%",
      position: "relative" as const,
    },
    header: {
      textAlign: "center" as const,
      marginBottom: "60px",
      color: "white",
    },
    headerTitle: {
      fontSize: "48px",
      fontWeight: 800,
      marginBottom: "16px",
      letterSpacing: "-0.02em",
    },
    headerSubtitle: {
      fontSize: "20px",
      opacity: 0.9,
      fontWeight: 400,
    },
    comparisonContainer: {
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      gap: "40px",
      alignItems: "center",
      position: "relative" as const,
    },
    section: {
      background: "white",
      borderRadius: "24px",
      padding: "40px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
      position: "relative" as const,
      overflow: "hidden",
      minHeight: "500px",
      transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
    },
    beforeSection: {
      border: "3px solid #ef4444",
      animation: "shake 2s ease-in-out infinite",
    },
    afterSection: {
      border: "3px solid #32620e",
      animation: "glow 3s ease-in-out infinite",
    },
    sectionHeader: {
      textAlign: "center" as const,
      marginBottom: "32px",
      fontSize: "24px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
    },
    beforeHeader: {
      color: "#dc2626",
    },
    afterHeader: {
      color: "#32620e",
      transition: "color 0.3s",
    },
    statusIcon: {
      fontSize: "32px",
      animation: "bounce 2s ease-in-out infinite",
    },
    meetingContent: {
      position: "relative" as const,
      height: "400px",
    },
    chaoticNotes: {
      position: "relative" as const,
      height: "100%",
    },
    stickyNote: {
      position: "absolute" as const,
      borderRadius: "8px",
      padding: "12px",
      fontSize: "14px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      animation: "float 3s ease-in-out infinite",
      borderLeft: "4px solid #eab308",
      maxWidth: "180px",
    },
    scribble: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "200px",
      height: "100px",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 100'%3E%3Cpath d='M10,50 Q50,10 90,50 T170,50' stroke='%23ef4444' stroke-width='3' fill='none' stroke-linecap='round'/%3E%3Cpath d='M20,60 Q60,20 100,60 T180,60' stroke='%23ef4444' stroke-width='2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      opacity: "0.6",
      animation: "scribbleMove 2s ease-in-out infinite",
    },
    confusionMarks: {
      position: "absolute" as const,
      top: "10px",
      right: "10px",
      fontSize: "24px",
      color: "#ef4444",
      animation: "confusion 1.5s ease-in-out infinite",
    },
    organizedNotes: {
      position: "relative" as const,
      height: "100%",
      background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      borderRadius: "16px",
      padding: "24px",
    },
    noteItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px",
      background: "white",
      borderRadius: "12px",
      marginBottom: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      transform: "translateX(-100px)",
      opacity: "0",
      animation: "slideInRight 0.8s ease-out forwards",
      borderLeft: "4px solid #10b981",
      transition: "all 0.3s ease",
    },
    checkIcon: {
      width: "20px",
      height: "20px",
      background: "#10b981",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: "12px",
      fontWeight: "bold",
      flexShrink: 0,
    },
    noteText: {
      fontSize: "14px",
      color: "#374151",
      fontWeight: "500",
    },
    assignee: {
      marginLeft: "auto",
      fontSize: "12px",
      color: "#6b7280",
      background: "#f3f4f6",
      padding: "4px 8px",
      borderRadius: "12px",
      fontWeight: "500",
    },
    transformArrow: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      gap: "16px",
    },
    arrowIcon: {
      fontSize: "48px",
      color: "white",
      animation: "pulse 2s ease-in-out infinite",
      textShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
    },
    orindraLogo: {
      background: "white",
      color: "#667eea",
      padding: "12px 24px",
      borderRadius: "50px",
      fontWeight: "800",
      fontSize: "18px",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
      animation: "logoFloat 3s ease-in-out infinite",
    },
    magicSparkles: {
      position: "absolute" as const,
      top: "-10px",
      right: "-10px",
      fontSize: "24px",
      animation: "sparkle 2s ease-in-out infinite",
    },
    particle: {
      position: "absolute" as const,
      width: "6px",
      height: "6px",
      background: "white",
      borderRadius: "50%",
      opacity: "0.7",
      animation: "particleFloat 4s ease-in-out infinite",
      pointerEvents: "none" as const,
    },
  };

  const animations = `
@keyframes shake {
0%, 100% { transform: translateX(0); }
25% { transform: translateX(-2px) rotate(-0.5deg); }
75% { transform: translateX(2px) rotate(0.5deg); }
}
@keyframes glow {
0%, 100% { box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2); }
50% { box-shadow: 0 20px 60px rgba(50, 98, 14, 0.25); }
}
@keyframes bounce {
0%, 100% { transform: translateY(0); }
50% { transform: translateY(-10px); }
}
@keyframes float {
0%, 100% { transform: rotate(var(--rotation)) translateY(0); }
50% { transform: rotate(var(--rotation)) translateY(-8px); }
}
@keyframes scribbleMove {
0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
25% { transform: translate(-50%, -50%) rotate(2deg); }
75% { transform: translate(-50%, -50%) rotate(-2deg); }
}
@keyframes confusion {
0%, 100% { transform: rotate(0deg); }
25% { transform: rotate(-15deg); }
75% { transform: rotate(15deg); }
}
@keyframes slideInRight {
from {
transform: translateX(-100px);
opacity: 0;
}
to {
transform: translateX(0);
opacity: 1;
}
}
@keyframes sparkle {
0%, 100% {
transform: rotate(0deg) scale(1);
opacity: 1;
}
50% {
transform: rotate(180deg) scale(1.2);
opacity: 0.8;
}
}
@keyframes pulse {
0%, 100% { transform: scale(1); }
50% { transform: scale(1.1); }
}
@keyframes logoFloat {
0%, 100% { transform: translateY(0); }
50% { transform: translateY(-8px); }
}
@keyframes particleFloat {
0%, 100% {
transform: translateY(0) rotate(0deg);
opacity: 0.7;
}
50% {
transform: translateY(-20px) rotate(180deg);
opacity: 1;
}
}
@keyframes borderPulse {
0%, 100% { opacity: 0.7; }
50% { opacity: 1; }
}
.before-section:hover {
transform: scale(1.02);
box-shadow: 0 25px 70px rgba(239, 68, 68, 0.3);
}
.after-section:hover {
transform: scale(1.02);
box-shadow: 0 25px 70px rgba(16, 185, 129, 0.3);
}
.before-section::before {
content: '';
position: absolute;
top: -2px;
left: -2px;
right: -2px;
bottom: -2px;
background: linear-gradient(45deg, #ef4444, #f97316, #ef4444);
border-radius: 26px;
z-index: -1;
animation: borderPulse 3s ease-in-out infinite;
}
.after-section::before {
content: '';
position: absolute;
top: -2px;
left: -2px;
right: -2px;
bottom: -2px;
background: linear-gradient(45deg, #32620e, #254808, #32620e);
border-radius: 26px;
z-index: -1;
animation: borderPulse 3s ease-in-out infinite;
}
.sticky-note:nth-child(1) {
--rotation: -8deg;
top: 20px;
left: 20px;
animation-delay: 0s;
background: #fef08a;
border-left-color: #eab308;
}
.sticky-note:nth-child(2) {
--rotation: 12deg;
top: 80px;
right: 30px;
animation-delay: 0.5s;
background: #fecaca;
border-left-color: #ef4444;
}
.sticky-note:nth-child(3) {
--rotation: -15deg;
bottom: 120px;
left: 40px;
animation-delay: 1s;
background: #ddd6fe;
border-left-color: #8b5cf6;
}
.sticky-note:nth-child(4) {
--rotation: 5deg;
bottom: 40px;
right: 20px;
animation-delay: 1.5s;
background: #bbf7d0;
border-left-color: #10b981;
}
.note-item:nth-child(1) { animation-delay: 0.2s; }
.note-item:nth-child(2) { animation-delay: 0.4s; }
.note-item:nth-child(3) { animation-delay: 0.6s; }
.note-item:nth-child(4) { animation-delay: 0.8s; }
@media (max-width: 768px) {
.comparison-container {
grid-template-columns: 1fr !important;
gap: 30px !important;
}
.transform-arrow {
order: 2;
transform: rotate(90deg);
}
.before-section {
order: 1;
}
.after-section {
order: 3;
}
.section {
padding: 24px !important;
min-height: 400px !important;
}
.arrow-icon {
font-size: 36px !important;
}
.orindra-logo {
font-size: 14px !important;
padding: 8px 16px !important;
}
.header h1 {
font-size: 32px !important;
}
.header p {
font-size: 16px !important;
}
}
`;

  const customCSS = `
    .after-section:hover .after-header {
      color: white !important;
    }
    .before-section:hover .before-header {
      color: #000 !important;
    }
  `;

  const stickyNotes = [
    "Did anyone catch what Sarah said about the budget?",
    "I think Mike was supposed to do something by Friday?",
    "What exactly did we decide on the vendor?",
    "Who's taking notes today?",
  ];

  const organizedItems = [
    { text: "Sarah: Review Q3 budget proposal by Friday", assignee: "Sarah" },
    { text: "Mike: Schedule vendor demo for next Tuesday", assignee: "Mike" },
    {
      text: "Decision: Approved Option B for design direction",
      assignee: "Team",
    },
    {
      text: "Follow-up: Client presentation scheduled for Monday",
      assignee: "Alex",
    },
  ];

  return (
    <>
      <style>{animations}</style>
      <style>{customCSS}</style>
      <div style={styles.wrapper} className={className}>
        {/* Floating particles - exactly like HTML */}
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.particle,
              top: ["20%", "60%", "80%", "30%"][i],
              left: ["10%", "90%", "20%", "80%"][i],
              animationDelay: ["0s", "1s", "2s", "1.5s"][i],
            }}
          />
        ))}
        {/* Dynamic particles */}
        {Array.from({ length: particleCount - 4 }).map((_, i) => (
          <div
            key={`dynamic-${i}`}
            style={{
              ...styles.particle,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
            }}
          />
        ))}
        <div ref={containerRef} style={styles.container}>
          {showHeader && (
            <div style={styles.header}>
              <h1 style={styles.headerTitle}>
                The Transformation is Immediate
              </h1>
              <p style={styles.headerSubtitle}>
                See how Orindra transforms chaotic meetings into clear action
              </p>
            </div>
          )}
          <div
            style={styles.comparisonContainer}
            className="comparison-container"
          >
            {/* Before Section */}
            <div
              className="before-section"
              style={{
                ...styles.section,
                ...styles.beforeSection,
              }}
              onClick={() => handleSectionClick("before")}
            >
              <div
                className="before-header"
                style={{ ...styles.sectionHeader, ...styles.beforeHeader }}
              >
                <span style={styles.statusIcon}>😤</span>
                What your team sees now
              </div>
              <div style={styles.meetingContent}>
                <div style={styles.chaoticNotes}>
                  {stickyNotes.map((note, index) => (
                    <div
                      key={index}
                      className="sticky-note"
                      style={styles.stickyNote}
                    >
                      "{note}"
                    </div>
                  ))}
                  <div style={styles.scribble}></div>
                  <div style={styles.confusionMarks}>❓❓❓</div>
                </div>
              </div>
            </div>
            {/* Transform Arrow */}
            <div style={styles.transformArrow}>
              <div style={{ ...styles.arrowIcon, color: "#32620e" }}>→</div>
            </div>
            {/* After Section */}
            <div
              className="after-section"
              style={{
                ...styles.section,
                ...styles.afterSection,
              }}
              onClick={() => handleSectionClick("after")}
            >
              <div
                className="after-header"
                style={{ ...styles.sectionHeader, ...styles.afterHeader }}
              >
                <span style={styles.statusIcon}>✨</span>
                What they'll see with Orindra
              </div>
              <div style={styles.meetingContent}>
                <div style={styles.organizedNotes}>
                  {organizedItems.map((item, index) => (
                    <div
                      key={index}
                      className="note-item"
                      style={styles.noteItem}
                    >
                      <div style={styles.checkIcon}>✓</div>
                      <div style={styles.noteText}>{item.text}</div>
                      <div style={styles.assignee}>{item.assignee}</div>
                    </div>
                  ))}
                  <div style={styles.magicSparkles}>✨</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BeforeAfterComparison;
